/* Predictive search dropdown for the header search box.
 *
 * - Fetches the catalogue index once on first focus, via the Section Rendering
 *   API (/?sections=search-index), following &page=2,3,… until a short page,
 *   so it works for ANY catalogue size. Cached in sessionStorage.
 * - Matching: lowercase + tokenize the query → each token must match SOMEWHERE
 *   on the product (title / primary colour+synonyms+parent / metal / secondary
 *   colour+synonyms+parent / tags) via exact → prefix → fuzzy (Levenshtein).
 *   AND across tokens (every word must match). Ranked by match quality × field
 *   weight. Top 3 shown.
 * - Everything it knows comes from the live index, so new products / shades /
 *   synonyms / metals flow in with no code change.
 *
 * Coexists with theme.js's search open/close/submit handlers — this only adds
 * the suggestions; Enter with no highlighted result still submits to /search.
 */
(function () {
  'use strict';

  var form = document.querySelector('[data-search-form]');
  if (!form) return;
  var input = form.querySelector('input[type="search"]');
  var panel = form.querySelector('[data-search-results]');
  if (!input || !panel) return;

  var MAX = 3;            // results shown
  var MIN_CHARS = 2;      // don't search under this
  var DEBOUNCE = 120;
  var PAGE_SIZE = 250;    // must match the section's `paginate by`

  // Field weights (matcher ranking): title > primary colour > metal >
  // secondary colour > tags.
  var W_TITLE = 5, W_PRIMARY = 4, W_METAL = 3, W_SECONDARY = 2, W_TAGS = 1;

  var RAW = null;        // merged product array from the index
  var ENTRIES = null;    // built searchable entries
  var fetching = null;   // in-flight fetch promise
  var activeIdx = -1;    // keyboard-highlighted result

  // ---------- text normalization ----------
  function norm(s) {
    return (s == null ? '' : '' + s).toLowerCase()
      .normalize('NFKD').replace(/[̀-ͯ]/g, '');   // strip accents
  }
  function words(s) {
    return norm(s).split(/[^a-z0-9]+/).filter(Boolean);
  }

  // ---------- index fetch (multi-page, merged, cached) ----------
  function unwrap(html) {
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.getElementById('shopify-section-search-index');
      return JSON.parse(el ? el.textContent : html);
    } catch (e) { return []; }
  }
  function fetchAll() {
    if (fetching) return fetching;
    try {
      var cached = sessionStorage.getItem('pennie-search-raw');
      if (cached) { RAW = JSON.parse(cached); fetching = Promise.resolve(RAW); return fetching; }
    } catch (e) {}
    var all = [];
    function page(p) {
      return fetch('/?sections=search-index&page=' + p, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var arr = unwrap(d['search-index'] || '');
          all = all.concat(arr);
          return arr.length === PAGE_SIZE ? page(p + 1) : all;   // full page → maybe more
        });
    }
    fetching = page(1).then(function (arr) {
      RAW = arr;
      try { sessionStorage.setItem('pennie-search-raw', JSON.stringify(arr)); } catch (e) {}
      return arr;
    }).catch(function () { return []; });
    return fetching;
  }

  // ---------- build searchable entries ----------
  function wordSet(values) {
    var set = Object.create(null);
    (values || []).forEach(function (v) { words(v).forEach(function (w) { set[w] = 1; }); });
    return Object.keys(set);
  }
  function build(raw) {
    return raw.map(function (p) {
      var primary = [];
      if (p.primary) {
        primary.push(p.primary.label, p.primary.parent);
        primary = primary.concat(p.primary.syn || []);
      }
      var secondary = [];
      (p.secondary || []).forEach(function (s) {
        secondary.push(s.label, s.parent);
        secondary = secondary.concat(s.syn || []);
      });
      return {
        p: p,
        fields: [
          { w: W_TITLE,     words: wordSet([p.title]) },
          { w: W_PRIMARY,   words: wordSet(primary) },
          { w: W_METAL,     words: wordSet(p.metal) },
          { w: W_SECONDARY, words: wordSet(secondary) },
          { w: W_TAGS,      words: wordSet(p.tags) }
        ]
      };
    });
  }

  // ---------- fuzzy matching ----------
  // allowed edit distance scales with token length (short words = stricter)
  function maxDist(len) { return len <= 3 ? 0 : (len <= 6 ? 1 : 2); }
  function lev(a, b, max) {
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    var prev = [], i, j;
    for (j = 0; j <= lb; j++) prev[j] = j;
    for (i = 1; i <= la; i++) {
      var cur = [i], best = i, ai = a.charCodeAt(i - 1);
      for (j = 1; j <= lb; j++) {
        var cost = ai === b.charCodeAt(j - 1) ? 0 : 1;
        var v = prev[j] + 1, l = cur[j - 1] + 1, d = prev[j - 1] + cost;
        cur[j] = v < l ? (v < d ? v : d) : (l < d ? l : d);
        if (cur[j] < best) best = cur[j];
      }
      if (best > max) return max + 1;   // whole row already past the budget
      prev = cur;
    }
    return prev[lb];
  }
  // 3 exact, 2 prefix, 1 fuzzy, 0 no match
  function wordQuality(token, word, md) {
    if (token === word) return 3;
    if (word.length > token.length && word.lastIndexOf(token, 0) === 0) return 2;
    if (md > 0 && lev(token, word, md) <= md) return 1;
    return 0;
  }
  // best (quality × field weight) for one token across all the product's fields
  function tokenScore(token, entry) {
    var md = maxDist(token.length), best = 0, f, fld, k, q, qq, sc;
    for (f = 0; f < entry.fields.length; f++) {
      fld = entry.fields[f];
      q = 0;
      for (k = 0; k < fld.words.length; k++) {
        qq = wordQuality(token, fld.words[k], md);
        if (qq > q) { q = qq; if (q === 3) break; }
      }
      if (q > 0) { sc = q * fld.w; if (sc > best) best = sc; }
    }
    return best;   // 0 = this token didn't match this product
  }

  function runSearch(query) {
    var tokens = words(query);
    if (!tokens.length || !ENTRIES) return [];
    var out = [], i, t, e, total, ok, s;
    for (i = 0; i < ENTRIES.length; i++) {
      e = ENTRIES[i]; total = 0; ok = true;
      for (t = 0; t < tokens.length; t++) {
        s = tokenScore(tokens[t], e);
        if (s === 0) { ok = false; break; }   // AND: every token must land somewhere
        total += s;
      }
      if (ok) out.push({ p: e.p, score: total });
    }
    out.sort(function (a, b) { return b.score - a.score || a.p.title.localeCompare(b.p.title); });
    return out.slice(0, MAX).map(function (r) { return r.p; });
  }

  // ---------- render ----------
  function esc(s) {
    return ('' + (s == null ? '' : s)).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function show() { panel.hidden = false; input.setAttribute('aria-expanded', 'true'); }
  function hide() {
    panel.hidden = true; panel.innerHTML = ''; activeIdx = -1;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  }
  function render(items, query) {
    activeIdx = -1;
    if (!query) { hide(); return; }
    if (!items.length) {
      panel.innerHTML = '<p class="header__search-empty">No matches for &ldquo;' + esc(query) + '&rdquo;</p>';
      show();
      return;
    }
    panel.innerHTML = items.map(function (p, i) {
      var img = p.img
        ? '<img class="search-result__img" src="' + esc(p.img) + '" alt="" loading="lazy" width="44" height="44">'
        : '<span class="search-result__img" aria-hidden="true"></span>';
      return '<a class="search-result" role="option" id="search-opt-' + i + '" href="' + esc(p.url) + '">' +
        img +
        '<span class="search-result__info">' +
          '<span class="search-result__title">' + esc(p.title) + '</span>' +
          '<span class="search-result__price desktop-price">' + esc(p.price) + '</span>' +
        '</span></a>';
    }).join('');
    show();
  }

  function opts() { return panel.querySelectorAll('.search-result'); }
  function setActive(idx) {
    var list = opts();
    if (!list.length) { activeIdx = -1; return; }
    activeIdx = (idx + list.length) % list.length;
    for (var i = 0; i < list.length; i++) list[i].classList.toggle('is-active', i === activeIdx);
    var act = list[activeIdx];
    act.scrollIntoView({ block: 'nearest' });
    input.setAttribute('aria-activedescendant', act.id);
  }

  // ---------- events ----------
  var timer;
  function update() {
    var q = input.value.trim();
    clearTimeout(timer);
    if (q.length < MIN_CHARS) { hide(); return; }
    timer = setTimeout(function () {
      fetchAll().then(function (raw) {
        if (!ENTRIES && raw && raw.length) ENTRIES = build(raw);
        var cur = input.value.trim();
        if (cur.length < MIN_CHARS) { hide(); return; }
        render(runSearch(cur), cur);
      });
    }, DEBOUNCE);
  }

  input.addEventListener('input', update);
  input.addEventListener('focus', function () {
    fetchAll();                                   // warm the index
    if (input.value.trim().length >= MIN_CHARS) update();
  });
  input.addEventListener('keydown', function (e) {
    if (panel.hidden) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIdx + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIdx - 1); }
    else if (e.key === 'Enter') {
      var list = opts();
      if (activeIdx >= 0 && list[activeIdx]) { e.preventDefault(); window.location.href = list[activeIdx].getAttribute('href'); }
      // else: let the form submit to /search (full results page)
    } else if (e.key === 'Escape') { hide(); }
  });

  // Hide on outside click, and whenever theme.js closes the search panel.
  document.addEventListener('click', function (e) { if (!form.contains(e.target)) hide(); });
  new MutationObserver(function () {
    if (!form.classList.contains('is-open')) hide();
  }).observe(form, { attributes: true, attributeFilter: ['class'] });
})();
